import React, { useState, useEffect, useRef } from 'react';
import { Chess } from 'chess.js';
import { fetchLichessPuzzles } from '../utils/homeworkData'; 
import { playSound, PIECE_SETS, BOARD_THEMES } from '../components/Board/BoardConstants'; 
import SkeletonLoader from '../components/SkeletonLoader';
import confetti from 'canvas-confetti'; 
import toast from 'react-hot-toast';
import { db } from '../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';

const LICHESS_CATEGORIES = [
    {
        title: "Beginner Basics (Kids)",
        themes: [
            { id: "hangingpiece", name: "Free Piece", icon: "🍎", desc: "Capture undefended enemy pieces." },
            { id: "matein1", name: "Mate in 1", icon: "🥇", desc: "Win the game in one single move!" },
            { id: "fork", name: "Knight Fork", icon: "🔱", desc: "Attack two pieces at once." },
            { id: "pin", name: "The Pin", icon: "📌", desc: "Freeze an enemy piece." }
        ]
    },
    {
        title: "Intermediate Tactics",
        themes: [
            { id: "matein2", name: "Mate in 2", icon: "🥈", desc: "Deliver checkmate in two moves." },
            { id: "skewer", name: "Skewer", icon: "🍢", desc: "Attack a high-value piece." },
            { id: "discoveredattack", name: "Discovered Attack", icon: "👻", desc: "Unleash a hidden attack." },
            { id: "deflection", name: "Deflection", icon: "🧲", desc: "Distract the defender." }
        ]
    }
];

export default function ChessWorlds({ user }) { 
    const [activeTheme, setActiveTheme] = useState(null); 
    const [puzzles, setPuzzles] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(false);

    const [game, setGame]               = useState(new Chess());
    const [selectedSq, setSelectedSq]   = useState(null);
    const [legalMovesP, setLegalMovesP] = useState([]);
    
    // 🔥 NEW: Player Orientation State
    const [playerColor, setPlayerColor] = useState('w'); 

    const [puzzleStatus, setPuzzleStatus] = useState('playing'); 
    const [statusMessage, setStatusMessage] = useState('');
    const [lastMoveHighlight, setLastMoveHighlight] = useState({ from: null, to: null });
    const [hintSquare, setHintSquare] = useState(null);
    const [solutionRemaining, setSolutionRemaining] = useState([]);
    const [shakeBoard, setShakeBoard] = useState(false); 

    // User preferences states
    const [boardTheme, setBoardTheme] = useState('green');
    const [pieceTheme, setPieceTheme] = useState('cburnett');

    // View options states
    const [is3D, setIs3D] = useState(false);
    const [showCoordinates, setShowCoordinates] = useState(true);
    const [showLegalDots, setShowLegalDots] = useState(true);

    // Drag and drop pointer states
    const [dragSq, setDragSq] = useState(null);
    const [dragPos, setDragPos] = useState(null);
    const boardRef = useRef(null);
    const isDraggingRef = useRef(false);
    const pointerDownPosRef = useRef(null);
    const pointerDownOffsetRef = useRef({ x: 0, y: 0 });

    const timeoutRef = useRef(null);
    const justDraggedRef = useRef(false);

    // Fetch user preferences in real time
    useEffect(() => {
        if (!user?.email) return;
        const unsub = onSnapshot(doc(db, "users", user.email.toLowerCase()), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.boardTheme) setBoardTheme(data.boardTheme);
                if (data.pieceTheme) setPieceTheme(data.pieceTheme);
            }
        });
        return () => unsub();
    }, [user]);

    useEffect(() => {
        return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
    }, []);

    const startTraining = async (theme) => {
        setLoading(true);
        setActiveTheme(theme);
        setCurrentIndex(0);
        try {
            const data = await fetchLichessPuzzles(theme.id, 15); 
            setPuzzles(data);
            if(data.length > 0) loadPuzzle(data[0]);
            else toast.error("No beginner puzzles found for this theme.");
        } catch (e) {
            toast.error("Failed to load puzzles.");
        }
        setLoading(false);
    };

    const loadPuzzle = (puzzleObj) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        try {
            const temp = new Chess(puzzleObj.fen);
            setGame(new Chess(puzzleObj.fen)); 
            
            setSelectedSq(null); setLegalMovesP([]); 
            setLastMoveHighlight({from: null, to: null}); setHintSquare(null);

            // 🔥 SMART PLAYER COLOR DETECTION
            const fenTurn = temp.turn(); // 'w' or 'b'
            // If opponent moves first, our color is opposite. Else, it's the fen's turn.
            const myColor = puzzleObj.opponentMove ? (fenTurn === 'w' ? 'b' : 'w') : fenTurn;
            setPlayerColor(myColor);

            if (puzzleObj.opponentMove) {
                setPuzzleStatus('opponent_moving'); 
                setStatusMessage('⏳ Opponent is making their move...');

                timeoutRef.current = setTimeout(() => {
                    try {
                        const oppMove = puzzleObj.opponentMove;
                        const moveRes = temp.move({ from: oppMove.substring(0, 2), to: oppMove.substring(2, 4), promotion: oppMove[4] || 'q' });
                        
                        if (moveRes) {
                            playSound(moveRes.captured ? 'capture' : 'move'); 
                            setGame(new Chess(temp.fen())); 
                            setLastMoveHighlight({ from: oppMove.substring(0, 2), to: oppMove.substring(2, 4) });
                        }
                        setSolutionRemaining(puzzleObj.solution || []);
                        setPuzzleStatus('playing'); 
                        setStatusMessage('Your turn. Find the best move!');
                    } catch (err) {
                        setSolutionRemaining(puzzleObj.solution || []);
                        setPuzzleStatus('playing');
                        setStatusMessage('Your turn. Find the best move!');
                    }
                }, 800);
            } else {
                setSolutionRemaining(puzzleObj.solution || []);
                setPuzzleStatus('playing');
                setStatusMessage('Your turn. Find the best move!');
            }
        } catch (e) { console.error("Error loading puzzle", e); }
    };

    const triggerError = () => {
        playSound('error'); 
        setShakeBoard(true);
        setTimeout(() => setShakeBoard(false), 500);
        setPuzzleStatus('failed'); 
        setStatusMessage('Oops! That is incorrect.');
        setSelectedSq(null); setLegalMovesP([]); setHintSquare(null);
    };

    const playComputerReply = (currentGame, currentSolution) => {
        if (currentSolution.length === 0) return;
        const reply = currentSolution[0];
        try {
            const temp2 = new Chess(currentGame.fen());
            const m = temp2.move({ from: reply.substring(0, 2), to: reply.substring(2, 4), promotion: reply[4] || 'q' });
            if (m) playSound(m.captured ? 'capture' : 'move'); 
            
            setGame(temp2); 
            setLastMoveHighlight({ from: reply.substring(0, 2), to: reply.substring(2, 4) });
            
            const newSolution = currentSolution.slice(1);
            setSolutionRemaining(newSolution);

            if (newSolution.length === 0) { 
                setPuzzleStatus('solved'); setStatusMessage('Success!'); 
                playSound('notify'); confetti(); 
            } else { 
                setPuzzleStatus('playing'); setStatusMessage('Keep going...'); 
            }
        } catch (e) { console.error("Auto-reply error", e); }
    };

    const isFlipped = playerColor === 'b';
    const rows = isFlipped ? [1,2,3,4,5,6,7,8] : [8,7,6,5,4,3,2,1];
    const cols = isFlipped ? ['h','g','f','e','d','c','b','a'] : ['a','b','c','d','e','f','g','h'];
    const BOARD_PX = Math.min(500, window.innerWidth - 40);

    const pixelToSquare = (clientX, clientY) => {
        if (!boardRef.current) return null;
        const rect = boardRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        if (x < 0 || y < 0 || x > BOARD_PX || y > BOARD_PX) return null;
        const sqSize = BOARD_PX / 8;
        const cIdx = Math.floor(x / sqSize);
        const rIdx = Math.floor(y / sqSize);
        if (cIdx < 0 || cIdx > 7 || rIdx < 0 || rIdx > 7) return null;
        return `${cols[cIdx]}${rows[rIdx]}`;
    };

    const handlePointerDown = (e, id) => {
        if (puzzleStatus !== 'playing' && puzzleStatus !== 'failed') return;
        const piece = game.get(id);
        if (!piece || piece.color !== playerColor) return;
        
        isDraggingRef.current = false;
        pointerDownPosRef.current = { x: e.clientX, y: e.clientY };
        
        const rect = boardRef.current.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;
        
        const cIdx = cols.indexOf(id[0]);
        const rIdx = rows.indexOf(parseInt(id[1]) || id[1]);
        const sqSize = BOARD_PX / 8;
        const pieceLeft = cIdx * sqSize;
        const pieceTop = rIdx * sqSize;
        pointerDownOffsetRef.current = { x: cursorX - pieceLeft, y: cursorY - pieceTop };
        
        setDragSq(id);
        setDragPos({ x: cursorX, y: cursorY });
        
        if (selectedSq !== id) {
            setSelectedSq(id);
            setLegalMovesP(game.moves({ square: id, verbose: true }).map(m => m.to));
        }
    };

    const attemptMove = (fromSq, toSq) => {
        if (puzzleStatus !== 'playing' && puzzleStatus !== 'failed') return;
        try {
            const temp = new Chess(game.fen());
            const move = temp.move({ from: fromSq, to: toSq, promotion: 'q' });
            
            if (move) {
                const firstMove = solutionRemaining[0]; 
                const playerUCI = `${fromSq}${toSq}`;
                
                if (firstMove && playerUCI === firstMove.substring(0, 4)) {
                    playSound(move.captured ? 'capture' : 'move'); 
                    setGame(temp); 
                    setLastMoveHighlight({ from: fromSq, to: toSq });
                    setSelectedSq(null); setLegalMovesP([]); setHintSquare(null);
                    
                    const newSol = solutionRemaining.slice(1);
                    setSolutionRemaining(newSol);

                    if (newSol.length === 0) { 
                        setPuzzleStatus('solved'); setStatusMessage('Brilliant Move! 🌟'); 
                        playSound('notify'); confetti(); 
                    } else {
                        setPuzzleStatus('computer_thinking'); setStatusMessage('Correct! Opponent replying...');
                        timeoutRef.current = setTimeout(() => playComputerReply(temp, newSol), 600);
                    }
                } else {
                    triggerError(); 
                }
            } else {
                triggerError(); 
            }
        } catch (e) { triggerError(); }
    };

    const handlePuzzleClick = (id) => {
        if (justDraggedRef.current) return;
        if (puzzleStatus !== 'playing' && puzzleStatus !== 'failed') return;

        if (selectedSq && legalMovesP.includes(id)) {
            attemptMove(selectedSq, id); 
        } else {
            const piece = game.get(id); const turn = game.turn();
            if (piece && piece.color === turn) {
                setSelectedSq(id); setLegalMovesP(game.moves({ square: id, verbose: true }).map(m => m.to));
            } else { setSelectedSq(null); setLegalMovesP([]); }
        }
    };

    const showHint = () => {
        if (solutionRemaining.length > 0) { 
            setHintSquare(solutionRemaining[0].substring(0, 2)); 
            setStatusMessage("💡 Hint: Move the highlighted piece."); 
        }
    };

    const viewSolution = () => {
        setPuzzleStatus('viewing'); setStatusMessage('Viewing Solution...');
        setHintSquare(null); setSelectedSq(null); setLegalMovesP([]);

        let currentTempGame = new Chess(game.fen());
        let solArray = [...solutionRemaining];
        let stepIndex = 0;

        const playNextStep = () => {
            if (stepIndex >= solArray.length) { 
                setStatusMessage('Solution completed.'); 
                return; 
            }
            const moveUci = solArray[stepIndex];
            try {
                const m = currentTempGame.move({ from: moveUci.substring(0,2), to: moveUci.substring(2,4), promotion: moveUci[4] || 'q' });
                if(m) playSound(m.captured ? 'capture' : 'move'); 
                
                setGame(new Chess(currentTempGame.fen())); 
                setLastMoveHighlight({ from: moveUci.substring(0,2), to: moveUci.substring(2,4) });
                
                stepIndex++; 
                timeoutRef.current = setTimeout(playNextStep, 900); 
            } catch(e) {}
        };
        playNextStep();
    };

    const loadNextPuzzle = () => {
        if (currentIndex + 1 < puzzles.length) {
            const nextIdx = currentIndex + 1;
            setCurrentIndex(nextIdx);
            loadPuzzle(puzzles[nextIdx]);
        } else {
            toast.success("Session Complete! Loading more...");
            startTraining(activeTheme); 
        }
    };

    // 🌐 GLOBAL DRAG-AND-DROP POINTER TRACKING (LICHESS STYLE)
    useEffect(() => {
        if (!dragSq) return;

        const handleGlobalPointerMove = (e) => {
            if (!pointerDownPosRef.current) return;
            const dx = e.clientX - pointerDownPosRef.current.x;
            const dy = e.clientY - pointerDownPosRef.current.y;
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
                isDraggingRef.current = true;
            }
            if (boardRef.current) {
                const rect = boardRef.current.getBoundingClientRect();
                setDragPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
            }
        };

        const handleGlobalPointerUp = (e) => {
            const targetSq = pixelToSquare(e.clientX, e.clientY);
            const wasDragMove = isDraggingRef.current && targetSq && targetSq !== dragSq;
            const draggedId = dragSq;

            if (isDraggingRef.current) {
                justDraggedRef.current = true;
                setTimeout(() => { justDraggedRef.current = false; }, 50);
            }

            // Clear drag states
            setDragSq(null);
            setDragPos(null);
            pointerDownPosRef.current = null;

            if (wasDragMove && legalMovesP.includes(targetSq)) {
                attemptMove(draggedId, targetSq);
            }
        };

        window.addEventListener('pointermove', handleGlobalPointerMove);
        window.addEventListener('pointerup', handleGlobalPointerUp);

        return () => {
            window.removeEventListener('pointermove', handleGlobalPointerMove);
            window.removeEventListener('pointerup', handleGlobalPointerUp);
        };
    }, [dragSq, legalMovesP]);

    if (activeTheme) {
        if (loading) return <SkeletonLoader />;

        if (puzzles.length === 0) {
            return (
                <div style={{textAlign: 'center', padding: '50px', color: 'var(--text-primary)' }}>
                    <h2>📭 No Easy Puzzles Found</h2>
                    <p>Could not find beginner puzzles for "{activeTheme.name}".</p>
                    <button onClick={() => setActiveTheme(null)} className="lichess-btn" style={{width: 'auto', margin: '20px auto', border: '1px solid rgba(163, 177, 198, 0.4)' }}>⬅ Back</button>
                </div>
            );
        }

        // Retrieve board theme and piece set
        const { light: lightSquare, dark: darkSquare } = BOARD_THEMES[boardTheme] || BOARD_THEMES.green;
        const isTheme3D = pieceTheme && pieceTheme.endsWith('3d');
        const active3D = is3D || isTheme3D;
        const currentPieceSet = active3D ? (isTheme3D ? (PIECE_SETS[pieceTheme] || PIECE_SETS.staunton3d) : PIECE_SETS.staunton3d) : (PIECE_SETS[pieceTheme] || PIECE_SETS.cburnett);

        const renderBoard = {};
        game.board().forEach((row, rIdx) => row.forEach((sq, cIdx) => {
            if (sq) renderBoard[`${['a','b','c','d','e','f','g','h'][cIdx]}${8 - rIdx}`] = sq.color + sq.type.toUpperCase();
        }));
        
        const highlightYellow = 'rgba(255, 255, 0, 0.4)'; const hintGlow = 'rgba(56, 189, 248, 0.8)'; 
        const currentPuzzle = puzzles[currentIndex];

        return (
            <div style={{width: '100%', maxWidth: '1000px', margin: '0 auto', padding: '20px 10px', animation: 'fadeIn 0.5s ease-out' }}>
                <style>{`
                    @keyframes fadeIn { from {opacity: 0;} to {opacity: 1;} }
                    @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px) rotate(-1deg); } 75% { transform: translateX(5px) rotate(1deg); } }
                    .shake-anim { animation: shake 0.3s ease-in-out; border: 4px solid #ef4444 !important; }
                    .lichess-btn { background: transparent; border: none; color: var(--text-secondary); padding: 15px 20px; font-weight: bold; cursor: pointer; transition: 0.2s; width: 100%; text-align: left; display: flex; align-items: center; gap: 12px; border-top: 1px solid rgba(163, 177, 198, 0.4); font-size: 15px; }
                    .lichess-btn:hover { background: rgba(255,255,255,0.05); color: white; }
                `}</style>

                <div style={{display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'center' }}>
                    
                    <div ref={boardRef} 
                         className={shakeBoard ? 'shake-anim' : ''} 
                         style={{
                             position: 'relative', 
                             width: BOARD_PX, 
                             height: BOARD_PX, 
                             display: 'grid', 
                             gridTemplateColumns: 'repeat(8, 1fr)', 
                             gridTemplateRows: 'repeat(8, 1fr)', 
                             borderRadius: '8px', 
                             overflow: active3D ? 'visible' : 'hidden', 
                             boxShadow: 'var(--panel-shadow)', 
                             flexShrink: 0, 
                             border: '4px solid transparent', 
                             transition: 'transform 0.4s ease-out, border 0.2s', 
                             touchAction: 'none',
                             userSelect: 'none',
                             WebkitUserSelect: 'none'
                         }}>
                        {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                            const id = `${col}${row}`; const isDark = (rIdx + cIdx) % 2 === 1;
                            let bg = isDark ? darkSquare : lightSquare;
                            let isLastMove = (lastMoveHighlight.from === id || lastMoveHighlight.to === id);
                            let isHint = (hintSquare === id); let isSelected = (selectedSq === id);

                            return (
                                <div key={id} 
                                     onClick={() => handlePuzzleClick(id)} 
                                     style={{background: isHint ? hintGlow : isSelected ? highlightYellow : bg, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: (puzzleStatus === 'playing' || puzzleStatus === 'failed') ? 'pointer' : 'default' }}>
                                    
                                    {isLastMove && !isSelected && !isHint && <div style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: highlightYellow, pointerEvents: 'none' }}></div>}
                                    {showLegalDots && legalMovesP.includes(id) && <div style={{width: '30%', height: '30%', background: 'rgba(0,0,0,0.2)', borderRadius: '50%', position: 'absolute', zIndex: 5, pointerEvents: 'none' }} />}
                                    
                                    {/* COORDINATES */}
                                    {showCoordinates && cIdx === 0 && <span style={{position: 'absolute', top: 2, left: 3, fontSize: '11px', color: isDark ? lightSquare : darkSquare, fontWeight: 'bold', pointerEvents: 'none', zIndex: 5, opacity: 0.9 }}>{row}</span>}
                                    {showCoordinates && rIdx === 7 && <span style={{position: 'absolute', bottom: 1, right: 3, fontSize: '11px', color: isDark ? lightSquare : darkSquare, fontWeight: 'bold', pointerEvents: 'none', zIndex: 5, opacity: 0.9 }}>{col}</span>}
                                </div>
                            );
                        }))}
                        {Object.entries(renderBoard).map(([id, piece]) => {
                            const cIdx = cols.indexOf(id[0]); const rIdx = rows.indexOf(parseInt(id[1]));
                            const isDraggablePiece = (puzzleStatus === 'playing' || puzzleStatus === 'failed') && piece.startsWith(playerColor);
                            const isDragging = dragSq === id;
                            const leftVal = isDragging ? `${dragPos.x - pointerDownOffsetRef.current.x}px` : `${cIdx * 12.5}%`;
                            const topVal  = isDragging ? `${dragPos.y - pointerDownOffsetRef.current.y}px` : `${rIdx * 12.5}%`;
                            
                            return <img key={id} src={currentPieceSet[piece]} alt={piece} 
                                draggable={false}
                                onDragStart={e => e.preventDefault()}
                                onPointerDown={e => { if (isDraggablePiece) handlePointerDown(e, id); else handlePuzzleClick(id); }} 
                                style={{ 
                                    position: 'absolute', 
                                    width: '12.5%', 
                                    height: '12.5%', 
                                    left: leftVal, 
                                    top: topVal, 
                                    cursor: isDraggablePiece ? 'grab' : 'default', 
                                    zIndex: isDragging ? 100 : 10, 
                                    pointerEvents: 'auto', 
                                    filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.4))',
                                    touchAction: 'none',
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                    transform: active3D ? 'translateY(-10px)' : 'none',
                                    transformOrigin: 'bottom center',
                                    transition: isDragging ? 'none' : 'left 0.15s ease-out, top 0.15s ease-out'
                                }} />;
                        })}
                    </div>

                    <div style={{flex: 1, minWidth: '320px', maxWidth: '400px', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', borderRadius: '12px', overflow: 'hidden', boxShadow: 'var(--panel-shadow)' }}>
                        {/* 🔥 PERFECT SYNC TURN BANNER */}
                        <div style={{padding: '15px 20px', fontSize: '18px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '12px', color: 'var(--text-primary)', background: 'var(--bg-primary)', borderBottom: '1px solid rgba(163, 177, 198, 0.4)' }}>
                            <span style={{width: '20px', height: '20px', borderRadius: '50%', background: playerColor === 'w' ? 'var(--text-primary)' : '#000000', border: '2px solid #64748b', display: 'inline-block' }}></span>
                            {puzzleStatus === 'opponent_moving' 
                                ? "Waiting for Opponent..." 
                                : `You are playing as ${playerColor === 'w' ? 'White' : 'Black'}`}
                        </div>

                        {/* ⚙️ BOARD VIEW OPTIONS */}
                        <div style={{ padding: '12px 20px', background: 'var(--bg-primary)', borderBottom: '1px solid rgba(163, 177, 198, 0.3)', display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                            <button onClick={() => setIs3D(!is3D)} className="kid-btn-bubble" style={{ flex: 1, padding: '8px 6px', fontSize: '11px', fontWeight: '700', border: 'none', borderRadius: '50px', background: active3D ? '#2563eb' : 'var(--bg-primary)', color: active3D ? '#fff' : 'var(--text-secondary)', boxShadow: 'var(--neu-shadow)', cursor: 'pointer', outline: 'none' }}>
                                {active3D ? '👁️ 3D View' : '👁️ 2D View'}
                            </button>
                            <button onClick={() => setShowCoordinates(!showCoordinates)} className="kid-btn-bubble" style={{ flex: 1, padding: '8px 6px', fontSize: '11px', fontWeight: '700', border: 'none', borderRadius: '50px', background: showCoordinates ? '#10b981' : 'var(--bg-primary)', color: showCoordinates ? '#fff' : 'var(--text-secondary)', boxShadow: 'var(--neu-shadow)', cursor: 'pointer', outline: 'none' }}>
                                {showCoordinates ? '🔢 Label On' : '🔢 Label Off'}
                            </button>
                            <button onClick={() => setShowLegalDots(!showLegalDots)} className="kid-btn-bubble" style={{ flex: 1, padding: '8px 6px', fontSize: '11px', fontWeight: '700', border: 'none', borderRadius: '50px', background: showLegalDots ? '#f59e0b' : 'var(--bg-primary)', color: showLegalDots ? '#fff' : 'var(--text-secondary)', boxShadow: 'var(--neu-shadow)', cursor: 'pointer', outline: 'none' }}>
                                {showLegalDots ? '🎯 Dots On' : '🎯 Dots Off'}
                            </button>
                        </div>

                        <div style={{padding: '25px 20px', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <div style={{color: '#3b82f6', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                                Challenge: {activeTheme.name}
                            </div>
                            
                            {puzzleStatus === 'opponent_moving' ? (
                                <h2 style={{margin: '0 0 15px 0', fontSize: '20px', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{animation: 'spin 1s linear infinite' }}>⏳</span> Opponent is moving...
                                </h2>
                            ) : (
                                <h2 style={{margin: '0 0 15px 0', fontSize: '22px', color: puzzleStatus==='failed' ? '#ef4444' : 'var(--text-primary)' }}>{statusMessage}</h2>
                            )}
                        </div>

                        <div style={{marginTop: 'auto' }}>
                            {puzzleStatus === 'failed' && <button className="lichess-btn" onClick={() => loadPuzzle(currentPuzzle)} style={{color: '#ef4444' }}><span style={{fontSize: '18px' }}>🔁</span> Try Again</button>}
                            {puzzleStatus === 'playing' && <button className="lichess-btn" onClick={showHint}><span style={{fontSize: '18px' }}>💡</span> Need a Hint?</button>}
                            {(puzzleStatus === 'playing' || puzzleStatus === 'failed') && <button className="lichess-btn" onClick={viewSolution}><span style={{fontSize: '18px' }}>👁️</span> Show Answer</button>}
                            
                            {(puzzleStatus === 'solved' || puzzleStatus === 'viewing') && (
                                <button className="lichess-btn" onClick={loadNextPuzzle} style={{background: '#10b981', color: 'black' }}>
                                    <span style={{fontSize: '18px' }}>⏭️</span> Next Puzzle
                                </button>
                            )}

                            <button className="lichess-btn" onClick={() => { setActiveTheme(null); setPuzzles([]); }}>
                                <span style={{fontSize: '18px' }}>⬅</span> Back to Menu
                            </button>
                        </div>

                        <div style={{padding: '12px 20px', background: 'var(--bg-primary)', fontSize: '12px', color: '#64748b', display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(163, 177, 198, 0.4)' }}>
                            <span>Difficulty: Kids/Beginner</span>
                            <span>Puzzle {currentIndex + 1} / {puzzles.length}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{width: '100%', padding: '20px 10px', position: 'relative', maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px', background: 'var(--bg-primary)', padding: '25px', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{fontSize: '50px', filter: 'drop-shadow(0 0 15px rgba(255,255,255,0.5))' }}>🎮</div>
                <div>
                    <h1 style={{color: 'var(--text-primary)', margin: 0, fontSize: '32px', letterSpacing: '1px' }}>Kids Puzzle Arcade</h1>
                    <p style={{color: 'var(--text-secondary)', margin: '5px 0 0 0', fontSize: '15px' }}>Simple, fun, and easy puzzles to learn how pieces move!</p>
                </div>
            </div>

            {LICHESS_CATEGORIES.map((category, idx) => (
                <div key={idx} style={{marginBottom: '40px' }}>
                    <h2 style={{color: 'var(--text-secondary)', fontSize: '18px', textTransform: 'uppercase', letterSpacing: '2px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px', marginBottom: '20px' }}>
                        {category.title}
                    </h2>
                    
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
                        {category.themes.map(theme => (
                            <div key={theme.id} onClick={() => startTraining(theme)}
                                className="glass-panel kid-card"
                                style={{
                                    background: 'var(--bg-primary)',
                                    border: '3px solid rgba(255, 255, 255, 0.95)',
                                    borderRadius: '24px',
                                    padding: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '20px',
                                    cursor: 'pointer',
                                    boxShadow: 'var(--panel-shadow)'
                                }}
                            >
                                <div style={{fontSize: '35px' }}>{theme.icon}</div>
                                <div>
                                    <h3 style={{color: 'var(--text-primary)', margin: '0 0 5px 0', fontSize: '16px' }}>{theme.name}</h3>
                                    <p style={{color: 'var(--text-secondary)', margin: 0, fontSize: '12px', lineHeight: '1.4' }}>{theme.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
